import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Cookies from 'js-cookie';
import { writeConsent, type AnalyticsConsent } from '../analytics/consent';
import { useAccount } from '../auth/AccountContext';
import { readConfigSettings, sanitizeConfigSettings, writeConfigSettings, type ReportFiltersConfig } from './configCookie';
import { fetchUserSettings, updateUserSettings } from './settingsClient';

export type ThemeModePref = 'light' | 'dark';

export interface LastOpenedPrefItem {
  path: string;
  label: string;
  openedAt: string; // ISO
}

export interface UserPreferences {
  themeMode: ThemeModePref;
  lastOpened: LastOpenedPrefItem[];
  navCollapsed: boolean;
  reportFilters: ReportFiltersConfig;
  analyticsConsent?: AnalyticsConsent;
}

interface PreferencesContextValue {
  prefs: UserPreferences | null;
  prefsReady: boolean;
  updatePrefs: (partial: Partial<UserPreferences>) => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

interface LoadedPrefs {
  sub: string;
  prefs: UserPreferences;
}

/** Legacy global key — migrated into per-user keys on first read. */
const LEGACY_STORAGE_KEY = 'hf_user_prefs_v1';
const THEME_COOKIE = 'hf_theme_mode';
const THEME_COOKIE_MAX_AGE_DAYS = 365;

function storageKeyFor(sub: string): string {
  return `${LEGACY_STORAGE_KEY}:${sub.trim()}`;
}

function asThemeMode(value: string | null | undefined): ThemeModePref | null {
  return value === 'light' || value === 'dark' ? value : null;
}

function asAnalyticsConsent(value: unknown): AnalyticsConsent | undefined {
  return value === 'granted' || value === 'denied' ? value : undefined;
}

function themeCookieBase(): Cookies.CookieAttributes {
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  return { sameSite: 'strict', path: '/', ...(secure ? { secure: true } : {}) };
}

/** Keep login / first paint in sync with the resolved user theme. */
function writeThemeCookie(mode: ThemeModePref): void {
  Cookies.set(THEME_COOKIE, mode, { ...themeCookieBase(), expires: THEME_COOKIE_MAX_AGE_DAYS });
}

function seedThemeMode(): ThemeModePref {
  const fromCookie = asThemeMode(Cookies.get(THEME_COOKIE));
  if (fromCookie) return fromCookie;

  return 'light';
}

function defaultUserPreferences(): UserPreferences {
  return {
    themeMode: seedThemeMode(),
    lastOpened: [],
    navCollapsed: false,
    reportFilters: readConfigSettings().reportFilters,
  };
}

function sanitizeLastOpened(value: unknown): LastOpenedPrefItem[] {
  if (!Array.isArray(value)) return [];
  const items: LastOpenedPrefItem[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const { path, label, openedAt } = entry as Record<string, unknown>;
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) continue;
    if (typeof label !== 'string' || !label.trim()) continue;
    if (typeof openedAt !== 'string' || Number.isNaN(Date.parse(openedAt))) continue;
    items.push({ path, label: label.trim(), openedAt });
  }
  return items;
}

function readStored(sub: string): UserPreferences | null {
  try {
    const key = storageKeyFor(sub);
    let raw = window.localStorage.getItem(key);
    if (!raw) {
      const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy) {
        raw = legacy;
        try {
          window.localStorage.setItem(key, legacy);
          window.localStorage.removeItem(LEGACY_STORAGE_KEY);
        } catch {
          // ignore migrate write failures
        }
      }
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    const defaults = defaultUserPreferences();
    const analyticsConsent = asAnalyticsConsent(parsed.analyticsConsent);
    return {
      themeMode: asThemeMode(parsed.themeMode) ?? defaults.themeMode,
      lastOpened: sanitizeLastOpened(parsed.lastOpened),
      navCollapsed: parsed.navCollapsed === true,
      reportFilters: sanitizeConfigSettings({ reportFilters: parsed.reportFilters }).reportFilters,
      ...(analyticsConsent ? { analyticsConsent } : {}),
    };
  } catch {
    return null;
  }
}

function writeStored(sub: string, prefs: UserPreferences): void {
  try {
    window.localStorage.setItem(storageKeyFor(sub), JSON.stringify(prefs));
  } catch {
    // ignore unavailable storage
  }
  // Mirror the sticky subset so pages can seed themselves before this async load resolves.
  writeConfigSettings({ reportFilters: prefs.reportFilters });
  writeThemeCookie(prefs.themeMode);
}

type TokenResolver = () => Promise<string | undefined>;

async function loadUserPreferences(sub: string, resolveToken: TokenResolver, onUnauthorized?: () => void): Promise<UserPreferences> {
  const cached = readStored(sub) ?? defaultUserPreferences();
  const remote = await fetchUserSettings(resolveToken);
  if (!remote.ok) {
    if (remote.status === 401) onUnauthorized?.();
    return cached;
  }

  const remoteConsent = asAnalyticsConsent(remote.data.analyticsConsent);
  const localConsent = remoteConsent ?? cached.analyticsConsent;
  // API is source of truth for theme + cookie consent after a successful load.
  const next: UserPreferences = {
    ...cached,
    themeMode: remote.data.themeMode,
    ...(localConsent ? { analyticsConsent: localConsent } : {}),
  };
  if (localConsent) writeConsent(localConsent, sub);
  if (!remoteConsent && localConsent) {
    void updateUserSettings(resolveToken, { analyticsConsent: localConsent });
  }
  writeStored(sub, next);
  return next;
}

async function saveUserPreferences(
  sub: string,
  prefs: UserPreferences,
  partial: Partial<UserPreferences>,
  resolveToken: TokenResolver,
  onUnauthorized?: () => void,
): Promise<UserPreferences> {
  writeStored(sub, prefs);

  const patch = {
    ...(partial.themeMode !== undefined ? { themeMode: prefs.themeMode } : {}),
    ...(partial.analyticsConsent !== undefined && prefs.analyticsConsent ? { analyticsConsent: prefs.analyticsConsent } : {}),
  };
  if (Object.keys(patch).length === 0) return prefs;

  const remote = await updateUserSettings(resolveToken, patch);
  if (remote.ok) {
    const next: UserPreferences = {
      ...prefs,
      themeMode: remote.data.themeMode,
      ...(asAnalyticsConsent(remote.data.analyticsConsent) ? { analyticsConsent: remote.data.analyticsConsent } : {}),
    };
    writeStored(sub, next);
    return next;
  }
  if (remote.status === 401) onUnauthorized?.();

  return prefs;
}

/** Must render inside AccountProvider — prefs are per authenticated user. */
export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { user, refreshIfNeeded, expireSession } = useAccount();
  const sub = user?.sub;
  const [loaded, setLoaded] = useState<LoadedPrefs | null>(null);
  const prefsRef = useRef<UserPreferences | null>(null);
  const refreshIfNeededRef = useRef(refreshIfNeeded);
  refreshIfNeededRef.current = refreshIfNeeded;

  useEffect(() => {
    // Keyed on sub (not the user object) so a token refresh does not refetch.
    if (!sub) return;

    let active = true;
    void loadUserPreferences(sub, () => refreshIfNeededRef.current(), expireSession).then((fetched) => {
      if (active) setLoaded({ sub, prefs: fetched });
    });

    return () => {
      active = false;
    };
  }, [sub, expireSession]);

  // Logout / account switch drops prefs by sub mismatch rather than by clearing state.
  const prefs = loaded && loaded.sub === sub ? loaded.prefs : null;
  const prefsReady = prefs !== null;

  useEffect(() => {
    prefsRef.current = prefs;
  }, [prefs]);

  const updatePrefs = useCallback(
    async (partial: Partial<UserPreferences>) => {
      if (!sub) return;

      const next = { ...(prefsRef.current ?? defaultUserPreferences()), ...partial };
      // Applied before the save resolves so controlled consumers (theme, nav rail) feel instant.
      prefsRef.current = next;
      setLoaded({ sub, prefs: next });

      const saved = await saveUserPreferences(sub, next, partial, () => refreshIfNeededRef.current(), expireSession);
      prefsRef.current = saved;
      setLoaded({ sub, prefs: saved });
    },
    [sub, expireSession],
  );

  const value = useMemo<PreferencesContextValue>(() => ({ prefs, prefsReady, updatePrefs }), [prefs, prefsReady, updatePrefs]);

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within a PreferencesProvider');
  return ctx;
}
